import React, { useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Container } from './Container';
import './App.css';

import { DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors, closestCorners, DragOverlay, defaultDropAnimationSideEffects } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';
import RoundEdgeButton from './RoundEdgeButton';

const DragNDropGrid = forwardRef(({ onSetSelectedItems }, ref) => {
    const initialItems = {
        itemsZone: ['beachBall', 'bottle', 'chess', 'dise', 'divesMask', 'dynamite', 'screwdriver', 'toyAircraft', 'toyTruck'],
        greenZone: []
    };
    const [items, setItems] = useState(initialItems);
    const [activeId, setActiveId] = useState(null);

    // Ref to track if we're currently processing a state update
    const isUpdatingRef = useRef(false);

    // Helper to find which container an item ID belongs to
    const findContainer = useCallback((id) => {
        if (id in items) {
            return id; // ID is a container ID
        }
        // Find the container the item ID is currently in
        return Object.keys(items).find((key) => items[key].includes(id));
    }, [items]);

    const addItems = useCallback((newItem) => {
        onSetSelectedItems(prevItems => {
            if (!prevItems.includes(newItem)) {
                return [...prevItems, newItem];
            }
            return prevItems;
        });
    }, [onSetSelectedItems]);

    const removeItems = useCallback((existItem) => {
        onSetSelectedItems(prevItems => {
            return prevItems.filter(item => item !== existItem);
        });
    }, [onSetSelectedItems]);

    const resetItems = () => {
        onSetSelectedItems([]);
    };

    // Configure sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragStart = useCallback((event) => {
        setActiveId(event.active.id);
    }, []);

    const handleDragOver = useCallback((event) => {
        const { active, over } = event;
        if (!over || isUpdatingRef.current) return; // Skip if we're already updating

        const activeId = active.id;
        const overId = over.id;

        if (activeId === overId) return;

        const activeContainer = findContainer(activeId);
        const overContainer = findContainer(overId);

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return; // Dragging within the same container is handled by onDragEnd's sort
        }

        isUpdatingRef.current = true;
        setItems((prev) => {
            const activeItems = [...prev[activeContainer]];
            const overItems = [...prev[overContainer]];

            const activeIndex = activeItems.indexOf(activeId);
            if (activeIndex === -1) {
                // Item not found where expected - abort this update
                isUpdatingRef.current = false;
                return prev;
            }

            // Remove from source
            activeItems.splice(activeIndex, 1);

            // Determine insert position
            let overIndex;
            if (overId in prev) {
                // Dropping on a container
                overIndex = overItems.length;
            } else {
                // Dropping on an item
                overIndex = overItems.indexOf(overId);
                if (overIndex === -1) {
                    // Target item not found - use end of container
                    overIndex = overItems.length;
                }
            }

            // Insert at position
            overItems.splice(overIndex, 0, activeId);

            // Release lock after a small delay (important for proper React scheduling)
            setTimeout(() => {
                isUpdatingRef.current = false;
            }, 50);

            return {
                ...prev,
                [activeContainer]: activeItems,
                [overContainer]: overItems,
            };
        });
    }, [findContainer]);

    // Refactored handleDragEnd to be simpler - cross-container moves handled by dragOver
    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        // Reset activeId for DragOverlay
        setActiveId(null);

        const activeId = active.id;
        const overId = over.id;

        const activeContainer = findContainer(activeId);
        const overContainer = findContainer(overId);

        if (!activeContainer || !overContainer) return;

        // Only handle same-container sorting here
        if (activeContainer === overContainer && activeId !== overId) {
            isUpdatingRef.current = true;
            setItems((prevItems) => {
                const containerItems = [...prevItems[activeContainer]];
                const oldIndex = containerItems.indexOf(activeId);
                const newIndex = containerItems.indexOf(overId);

                // Check indices are valid before moving
                if (oldIndex === -1 || newIndex === -1) {
                    isUpdatingRef.current = false;
                    return prevItems;
                }

                setTimeout(() => {
                    isUpdatingRef.current = false;
                }, 50);
                return {
                    ...prevItems,
                    [activeContainer]: arrayMove(containerItems, oldIndex, newIndex),
                };
            });
        }

        if (activeContainer === 'greenZone')
            addItems(activeId);
        else if (activeContainer === 'itemsZone')
            removeItems(activeId);

    }, [findContainer, addItems, removeItems]);

    // Reset function
    const handleReset = () => { resetItems(); setItems(initialItems); };

    // Expose handleReset to parent via ref
    useImperativeHandle(ref, () => ({
        handleReset
    }));


    // Customize drop animation
    const dropAnimation = {
        duration: 250,
        easing: 'ease',
        sideEffects: defaultDropAnimationSideEffects({
            styles: { active: { opacity: '0.5' } },
        }),
    };

    return (
        <div>
            <div style={{ textAlign: 'center' }}>
                <h4 style={{ marginTop: '-10px', marginBottom: '-10px' }}>
                    Look at the toys in the bottom-left of the big screen, then drag the same ones into the <span style={{ color: '#b9e875' }}>green zone</span>
                </h4>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
            >
                <div
                    style={{
                        display: 'grid',
                        flexWrap: 'wrap',
                        justifyContent: 'center'
                    }}
                >
                    {Object.keys(items).map((containerId) => (
                        <Container
                            key={containerId}
                            id={containerId}
                            items={items[containerId]}
                            isGreen={containerId === 'greenZone' ? true : false}
                        />
                    ))}
                    <div style={{ position: 'relative', left: '235px', display: 'flex', gap: '10px' }}>
                        <RoundEdgeButton onClick={handleReset}
                            variant='roundEdgeInBlack'>&#x21ba;</RoundEdgeButton>
                    </div>
                </div>
                <DragOverlay dropAnimation={dropAnimation}>
                    {activeId ? <SortableItem id={activeId} isDragging={true} /> : null}
                </DragOverlay>
            </DndContext>
        </div>
    );
});
export default DragNDropGrid;